/**
 * Owns the reducer, the API client, and every side effect: bootstrap, debounced service-area
 * checks, debounced/aborted price estimates, availability loading, and the submission sequence
 * (recaptcha → SetupIntent → card confirmation → POST /bookings) with 422 → pane mapping.
 * Framework-agnostic; React binds with useSyncExternalStore.
 */
import { loadBootstrap } from '../api/bootstrap';
import type { ApiClient } from '../api/client';
import {
  ApiError,
  NetworkError,
  ProductionGuardError,
  isAbortError,
  type FieldErrors,
} from '../api/errors';
import type { BookingMeta, CreateBookingResponse } from '../api/types';
import type { BillingDetails } from '../payments/stripe';
import type { ConditionalRule, RuntimeConfig } from '../config';
import { fetchSpots } from '../api/spots';
import { createEstimator } from './estimator';
import { createLogger, describeApiFailure, summariseApiFailure, type Logger } from '../diagnostics';
import { bookingEndpoint, captchaPlan } from '../domain/captcha';
import { stateNames } from '../domain/geo';
import { addDays, slotsFromSpots, tenantNow } from '../domain/dates';
import { findService } from '../domain/services';
import { buildBookingRequest, buildEstimateRequest, estimateKey } from '../domain/payload';
import { GENERIC, customerMessage, scrubFieldErrors } from '../domain/messages';
import {
  firstErrorKey,
  mapApiErrors,
  validateStep1,
  validateStep2,
  validateStep3,
  type Pane,
} from '../domain/validation';
import { initialState, lowestPane, reduce } from './machine';
import {
  selectCustomFieldIds,
  selectCustomFieldPlacement,
  selectRecaptchaRequired,
  selectRequireZip,
  selectTimeZone,
  selectVisibility,
} from './selectors';
import type { FormEvent, FormState } from './types';

export interface ControllerDeps {
  client: ApiClient;
  config: RuntimeConfig;
  rules?: ConditionalRule[];
  /** Runs Stripe's card confirmation for a SetupIntent client secret; resolves to the `seti_…` id. */
  confirmCardSetup?: (clientSecret: string, billing: BillingDetails) => Promise<string>;
  /** Provides the reCAPTCHA token when the tenant requires one (v2 widget response). */
  getRecaptchaToken?: () => Promise<string | null>;
  /** UTM/meta captured on landing; attached to the booking. */
  meta?: () => BookingMeta[];
  onSuccess?: (result: CreateBookingResponse, state: FormState) => void;
  /** Diagnostics sink; defaults to the console logger (verbose with ?cbf_debug=1). */
  logger?: Logger;
  now?: () => Date;
  debounceMs?: { estimate?: number; location?: number };
  spotsDays?: number;
}

export type Listener = (state: FormState) => void;

export function createFormController(deps: ControllerDeps) {
  const now = deps.now ?? (() => new Date());
  const log = deps.logger ?? createLogger(deps.config.debug);
  const debounce = { estimate: 400, location: 500, ...deps.debounceMs };
  const spotsDays = Math.min(45, Math.max(1, deps.spotsDays ?? 45));
  let state = initialState(deps.rules ?? deps.config.conditionals);
  const listeners = new Set<Listener>();
  let destroyed = false;

  // The estimator owns its own timer and abort controller (src/state/estimator.ts).
  const timers: { location: ReturnType<typeof setTimeout> | null } = { location: null };
  const aborts: {
    location: AbortController | null;
    spots: AbortController | null;
  } = { location: null, spots: null };

  function dispatch(event: FormEvent): FormState {
    if (destroyed) return state;
    const next = reduce(state, event);
    if (next !== state) {
      state = next;
      for (const l of listeners) l(state);
    }
    return state;
  }

  // ---------- bootstrap ----------

  async function start(): Promise<FormState> {
    try {
      const data = await loadBootstrap(
        deps.client,
        deps.config.formType,
        undefined,
        deps.config.settingsPreload,
        { stripeKeyOverride: deps.config.stripeKeyOverride, env: deps.config.env },
      );
      dispatch({ type: 'BOOT_OK', data, serviceStates: deps.config.serviceStates });
      // Deep link: /book/?service=<name slug or id> lands with that service already chosen.
      const preselected = findService(data.services, deps.config.preselectService);
      if (typeof preselected?.id === 'number')
        send({ type: 'SERVICE_SELECTED', serviceId: preselected.id });
      await loadSpots(tenantNow(selectTimeZone(state), now()).date);
    } catch (err) {
      dispatch({ type: 'BOOT_FAIL', message: customerMessage(err, GENERIC.load) });
    }
    return state;
  }

  // ---------- availability ----------

  async function loadSpots(from: string): Promise<void> {
    aborts.spots?.abort();
    const ctrl = (aborts.spots = new AbortController());
    dispatch({ type: 'SPOTS_LOADING', from });
    try {
      const days = await fetchSpots(
        deps.client,
        { date: from, days: spotsDays, mode: 'new', locationId: state.location.id },
        ctrl.signal,
      );
      if (ctrl.signal.aborted) return;
      dispatch({ type: 'SPOTS_OK', from, days });
    } catch (err) {
      if (isAbortError(err)) return;
      dispatch({ type: 'SPOTS_FAIL', from, message: customerMessage(err, GENERIC.load) });
    }
  }

  // ---------- service area ----------

  function locationKey(): string | null {
    const { address, city, state: st, zip } = state.values;
    if (zip.trim().length < 5) return null;
    return JSON.stringify({
      address: address.trim(),
      city: city.trim(),
      state: st.trim(),
      zip: zip.trim(),
    });
  }

  function scheduleLocationCheck(): void {
    if (timers.location) clearTimeout(timers.location);
    const key = locationKey();
    if (!key) return;
    timers.location = setTimeout(() => void checkLocation(key), debounce.location);
  }

  async function checkLocation(key: string): Promise<void> {
    if (key !== locationKey()) return;
    aborts.location?.abort();
    const ctrl = (aborts.location = new AbortController());
    dispatch({ type: 'LOCATION_CHECKING', key });
    const { address, city, state: st, zip } = state.values;
    try {
      const res = await deps.client.resolveLocation(
        {
          address: address.trim() || null,
          city: city.trim() || null,
          state: st.trim() || null,
          zip: zip.trim(),
        },
        ctrl.signal,
      );
      if (ctrl.signal.aborted) return;
      dispatch({
        type: 'LOCATION_OK',
        key,
        id: typeof res.id === 'number' ? res.id : defaultLocationId(),
      });
    } catch (err) {
      if (isAbortError(err)) return;
      const policy = state.bootstrap?.locationPolicy;
      if (
        err instanceof ApiError &&
        (err.isOutOfArea || (err.isValidation && policy?.prevent_booking === true))
      ) {
        // The tenant's own out-of-area text wins; otherwise a generic line — never the API's wording.
        dispatch({
          type: 'LOCATION_BLOCKED',
          key,
          message: policy?.prevent_booking_text?.trim() || GENERIC.outOfArea,
        });
      } else if (err instanceof ApiError && err.isValidation) {
        // out of area but the tenant doesn't block — fall back to the default zone
        dispatch({ type: 'LOCATION_OK', key, id: defaultLocationId() });
      } else {
        dispatch({ type: 'LOCATION_ERROR', key, message: customerMessage(err, GENERIC.failed) });
      }
    }
    if (state.pendingNext && state.location.status !== 'checking') void next();
  }

  function defaultLocationId(): number | null {
    const id = state.bootstrap?.settings.default_location?.id;
    return typeof id === 'number' ? id : null;
  }

  // ---------- pricing ----------

  const estimateContext = () => ({
    timeZone: selectTimeZone(state),
    now,
    locationId: state.location.id ?? defaultLocationId(),
  });

  const estimator = createEstimator({
    client: deps.client,
    debounceMs: debounce.estimate,
    build: () => buildEstimateRequest(state.values, estimateContext()),
    current: () => ({ key: state.estimate.key, status: state.estimate.status }),
    onLoading: (key) => void dispatch({ type: 'ESTIMATE_LOADING', key }),
    onOk: (key, data) => void dispatch({ type: 'ESTIMATE_OK', key, data }),
    onFail: (key, message, fieldErrors) =>
      void dispatch({ type: 'ESTIMATE_FAIL', key, message, fieldErrors }),
    onCleared: () => void dispatch({ type: 'ESTIMATE_CLEARED' }),
  });

  const scheduleEstimate = (): void => estimator.schedule();

  // ---------- navigation ----------

  function step1Errors(): FieldErrors {
    return validateStep1(state.values, state.bootstrap?.form ?? null, {
      requireZip: selectRequireZip(state),
      countryCode: state.bootstrap?.settings.country?.code ?? null,
      locationBlockedMessage: state.location.status === 'blocked' ? state.location.message : null,
      serviceStates: deps.config.serviceStates,
      serviceStateNames: stateNames(
        deps.config.serviceStates,
        state.bootstrap?.settings.country?.states,
      ),
    });
  }

  function step2Errors(): FieldErrors {
    return validateStep2({
      values: state.values,
      form: state.bootstrap?.form ?? null,
      services: state.bootstrap?.services ?? [],
      fields: selectCustomFieldPlacement(state).mainFields,
      visibleCodes: selectVisibility(state).visibleCodes,
    });
  }

  function currentCaptchaPlan() {
    return captchaPlan(
      selectRecaptchaRequired(state),
      deps.config.recaptchaSiteKey,
      deps.config.env,
    );
  }

  function step3Errors(): FieldErrors {
    const plan = currentCaptchaPlan();
    return validateStep3({
      values: state.values,
      fields: selectCustomFieldPlacement(state).beforeSubmitFields,
      visibleCodes: selectVisibility(state).visibleCodes,
      cardComplete: state.cardComplete,
      // 'blocked' also demands a token so a misconfigured live tenant stays unsubmittable.
      recaptchaRequired: plan === 'token' || plan === 'blocked',
      recaptchaToken: state.recaptchaToken,
    });
  }

  async function next(): Promise<FormState> {
    dispatch({ type: 'NEXT' });
    if (state.step === 1) {
      if (state.location.status === 'checking') {
        state = { ...state, pendingNext: true };
        return state;
      }
      if (locationKey() && state.location.status === 'idle') {
        // the customer typed fast enough to beat the debounce — check now and come back
        if (timers.location) clearTimeout(timers.location);
        state = { ...state, pendingNext: true };
        await checkLocation(locationKey()!);
        return state;
      }
      const errors = step1Errors();
      const focusKey = firstErrorKey(errors, 1);
      if (focusKey) return dispatch({ type: 'GO_TO_PANE', pane: 1, focusKey });
      return dispatch({ type: 'GO_TO_PANE', pane: 2 });
    }
    if (state.step === 2) {
      const errors = step2Errors();
      const focusKey = firstErrorKey(errors, 2, selectCustomFieldPlacement(state));
      if (focusKey) return dispatch({ type: 'GO_TO_PANE', pane: 2, focusKey });
      return dispatch({ type: 'GO_TO_PANE', pane: 3 });
    }
    return state;
  }

  function back(): FormState {
    return dispatch({ type: 'BACK' });
  }

  // ---------- submission ----------

  /** Marks a submission phase and records it in the debug trail. */
  function phase(name: FormState['submitPhase']): void {
    dispatch({ type: 'SUBMIT_PHASE', phase: name });
    log.debug(`submit: ${name}`);
  }

  async function submit(): Promise<FormState> {
    if (state.status !== 'ready') return state;
    dispatch({ type: 'SUBMIT_STARTED' });

    const stepErrors: Record<Pane, FieldErrors> = {
      1: step1Errors(),
      2: step2Errors(),
      3: step3Errors(),
    };
    const pane = ([1, 2, 3] as Pane[]).find((p) => Object.keys(stepErrors[p]).length) ?? null;
    if (pane) {
      return dispatch({
        type: 'SUBMIT_FAILED',
        fieldErrors: {},
        banner: [],
        pane,
        focusKey: firstErrorKey(stepErrors[pane], pane, selectCustomFieldPlacement(state)),
      });
    }

    const bootstrap = state.bootstrap!;
    const submittedIds: number[] = [];
    try {
      phase('recaptcha');
      const plan = currentCaptchaPlan();
      const endpoint = bookingEndpoint(plan);
      let recaptchaToken = '';
      if (plan === 'token' || plan === 'blocked') {
        recaptchaToken = state.recaptchaToken ?? (await deps.getRecaptchaToken?.()) ?? '';
        if (!recaptchaToken) {
          log.error('captcha token missing at submit', { plan });
          return fail({ recaptcha_token: [GENERIC.captcha] }, []);
        }
      }

      let stripeSetupIntentId: string | null = null;
      if (state.values.paymentMethod === 'stripe') {
        if (!deps.confirmCardSetup) throw new Error('Card payments are not available right now.');
        phase('setup_intent');
        const { stripe_setup_intent_secret } = await deps.client.createSetupIntent({
          email: state.values.user.email.trim(),
        });
        phase('confirm_card');
        try {
          const v = state.values;
          stripeSetupIntentId = await deps.confirmCardSetup(stripe_setup_intent_secret, {
            name: `${v.user.first_name.trim()} ${v.user.last_name.trim()}`.trim(),
            email: v.user.email.trim(),
            phone: v.phone.trim() || undefined,
            address: {
              line1: v.address.trim() || undefined,
              city: v.city.trim() || undefined,
              state: v.state.trim() || undefined,
              postal_code: v.zip.trim() || undefined,
              country: state.bootstrap?.settings.country?.code ?? undefined,
            },
          });
        } catch (err) {
          log.error('card verification failed', describeApiFailure(err));
          return fail({ card: [customerMessage(err, GENERIC.card)] }, []);
        }
      }

      phase('posting');
      const { request, customFieldIds } = buildBookingRequest(state.values, {
        fields: bootstrap.customFields,
        visibleCodes: selectVisibility(state).visibleCodes,
        locationId: state.location.id ?? defaultLocationId(),
        recaptchaToken,
        stripeSetupIntentId,
        meta: deps.meta?.() ?? [],
      });
      submittedIds.push(...customFieldIds);
      log.debug('POST booking', { endpoint, request });
      let result: CreateBookingResponse;
      if (endpoint === '/bookings') {
        result = await deps.client.createBooking(request);
      } else {
        // The captcha-less endpoint rejects nothing about the token — it just has no use for it.
        const { recaptcha_token: _omitted, ...withoutToken } = request;
        result = await deps.client.createBookingNoCaptcha(withoutToken as typeof request);
      }
      dispatch({ type: 'SUBMIT_OK', result });
      deps.onSuccess?.(result, state);
      return state;
    } catch (err) {
      if (err instanceof ApiError && err.isValidation) {
        const placement = selectCustomFieldPlacement(state);
        const { fieldErrors, banner, firstPane } = mapApiErrors(scrubFieldErrors(err.fieldErrors), {
          submittedCustomFieldIds: submittedIds,
          knownCustomFieldIds: selectCustomFieldIds(state),
          beforeSubmitCustomFieldIds: placement.beforeSubmitIds,
        });
        if (!Object.keys(fieldErrors).length && !banner.length)
          banner.push(customerMessage(err, GENERIC.failed));
        // `fields` are the API's own keys, `mappedTo` ours; anything that landed in the banner
        // instead of on a field is the signature of an error the form could not place.
        log.error(
          `booking rejected: ${summariseApiFailure(err, state.submitPhase)}` +
            (banner.length ? ` (${banner.length} could not be placed on a field)` : ''),
          {
            ...describeApiFailure(err),
            phase: state.submitPhase,
            mappedTo: Object.keys(fieldErrors),
            shownOn: firstPane,
            bannerCount: banner.length,
          },
        );
        log.debug('rejection detail', { fieldErrors: err.fieldErrors, body: err.body });
        return dispatch({
          type: 'SUBMIT_FAILED',
          fieldErrors,
          banner,
          pane: firstPane,
          focusKey: firstPane ? firstErrorKey(fieldErrors, firstPane, placement) : null,
        });
      }
      log.error(`booking failed: ${summariseApiFailure(err, state.submitPhase)}`, {
        ...describeApiFailure(err),
        phase: state.submitPhase,
      });
      log.debug('failure detail', { error: err });
      if (err instanceof ApiError && err.isRateLimited) return fail({}, [GENERIC.busy]);
      if (err instanceof NetworkError) return fail({}, [GENERIC.offline]);
      if (err instanceof ProductionGuardError) return fail({}, [err.message]);
      return fail({}, [customerMessage(err, GENERIC.failed)]);
    }

    /** Failures raised by the payment/captcha steps — all of those fields live on pane 3. */
    function fail(fieldErrors: FieldErrors, banner: string[]): FormState {
      const pane: Pane | null = Object.keys(fieldErrors).length ? 3 : null;
      return dispatch({
        type: 'SUBMIT_FAILED',
        fieldErrors,
        banner,
        pane,
        focusKey: pane ? firstErrorKey(fieldErrors, pane, selectCustomFieldPlacement(state)) : null,
      });
    }
  }

  // ---------- public dispatch with effects ----------

  const ESTIMATE_TRIGGERS = new Set<FormEvent['type']>([
    'SERVICE_SELECTED',
    'EXTRA_SET',
    'PRICING_PARAM_SET',
    'SLOT_SELECTED',
    'FREQUENCY_SELECTED',
    'DISCOUNT_CODE_APPLIED',
    'LOCATION_OK',
  ]);

  function send(event: FormEvent): FormState {
    const before = state;
    dispatch(event);
    if (state === before && event.type !== 'DISCOUNT_CODE_APPLIED') return state;
    if (ESTIMATE_TRIGGERS.has(event.type)) scheduleEstimate();
    if (event.type === 'ADDRESS_FIELD' && event.field !== 'phone') scheduleLocationCheck();
    return state;
  }

  function subscribe(listener: Listener): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  }

  function destroy(): void {
    destroyed = true;
    estimator.destroy();
    if (timers.location) clearTimeout(timers.location);
    aborts.location?.abort();
    aborts.spots?.abort();
    listeners.clear();
  }

  return {
    getState: () => state,
    subscribe,
    send,
    start,
    next,
    back,
    submit,
    loadSpots,
    /** exposed for tests */
    describeErrors: () => ({ step1: step1Errors(), step2: step2Errors(), step3: step3Errors() }),
    nextSpotsRange: () => (state.spots.from ? addDays(state.spots.from, spotsDays) : null),
    destroy,
  };
}

export type FormController = ReturnType<typeof createFormController>;
